import { Injectable, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  DocumentReference,
  getDocs
} from '@angular/fire/firestore';
import { Observable, from } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AdminUser, AdminUserRole, AdminUserStatus, DeviceInfo } from '../../shared/models';
import { createDeviceInfo } from '../../shared/utils/device-detection.util';

@Injectable({
  providedIn: 'root'
})
export class AdminUserService {
  private readonly collectionName = 'AdminUsers';

  constructor(
    private firestore: Firestore,
    private injector: EnvironmentInjector
  ) {}

  /**
   * Get all admin users
   */
  getAllUsers(): Observable<AdminUser[]> {
    return runInInjectionContext(this.injector, () => {
      const usersCollection = collection(this.firestore, this.collectionName) as any;
      const q = query(usersCollection, orderBy('createdAt', 'desc')) as any;
      return from(getDocs(q)).pipe(
        map(snapshot => {
          return snapshot.docs.map(doc => {
            const data = doc.data() as any;
            return { ...data, id: doc.id } as AdminUser;
          });
        })
      );
    });
  }

  /**
   * Get users by client ID
   */
  getUsersByClient(clientId: string): Observable<AdminUser[]> {
    return runInInjectionContext(this.injector, () => {
      const usersCollection = collection(this.firestore, this.collectionName) as any;
      const q = query(
        usersCollection,
        where('clientId', '==', clientId)
      ) as any;
      return from(getDocs(q)).pipe(
        map(snapshot => {
          return snapshot.docs.map(doc => {
            const data = doc.data() as any;
            return { ...data, id: doc.id } as AdminUser;
          });
        })
      );
    });
  }

  /**
   * Get users by role
   */
  getUsersByRole(role: AdminUserRole): Observable<AdminUser[]> {
    return runInInjectionContext(this.injector, () => {
      const usersCollection = collection(this.firestore, this.collectionName) as any;
      const q = query(
        usersCollection,
        where('role', '==', role),
        orderBy('createdAt', 'desc')
      ) as any;
      return collectionData(q, { idField: 'id' }) as Observable<AdminUser[]>;
    });
  }

  /**
   * Get users by status
   */
  getUsersByStatus(status: AdminUserStatus): Observable<AdminUser[]> {
    return runInInjectionContext(this.injector, () => {
      const usersCollection = collection(this.firestore, this.collectionName) as any;
      const q = query(
        usersCollection,
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      ) as any;
      return collectionData(q, { idField: 'id' }) as Observable<AdminUser[]>;
    });
  }

  /**
   * Get a single user by ID
   */
  getUserById(userId: string): Observable<AdminUser | undefined> {
    return runInInjectionContext(this.injector, () => {
      const userDoc = doc(this.firestore, `${this.collectionName}/${userId}`) as any;
      return docData(userDoc, { idField: 'id' }) as Observable<AdminUser | undefined>;
    });
  }

  /**
   * Get a user by email
   */
  getUserByEmail(email: string): Observable<AdminUser[]> {
    return runInInjectionContext(this.injector, () => {
      const usersCollection = collection(this.firestore, this.collectionName) as any;
      const q = query(usersCollection, where('email', '==', email)) as any;
      return collectionData(q, { idField: 'id' }) as Observable<AdminUser[]>;
    });
  }

  /**
   * Create a new admin user
   */
  async createUser(userData: Omit<AdminUser, 'id' | 'createdAt' | 'updatedAt'>): Promise<DocumentReference> {
    return await runInInjectionContext(this.injector, async () => {
      const usersCollection = collection(this.firestore, this.collectionName);
      const newUser = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      return await addDoc(usersCollection, newUser);
    });
  }

  /**
   * Update an existing admin user
   */
  async updateUser(userId: string, userData: Partial<AdminUser>): Promise<void> {
    return await runInInjectionContext(this.injector, async () => {
      const userDoc = doc(this.firestore, `${this.collectionName}/${userId}`);
      const updateData = {
        ...userData,
        updatedAt: serverTimestamp()
      };
      return await updateDoc(userDoc, updateData);
    });
  }

  /**
   * Update user status
   */
  async updateUserStatus(userId: string, status: AdminUserStatus): Promise<void> {
    return this.updateUser(userId, { status });
  }

  /**
   * Update last login timestamp
   */
  async updateLastLogin(userId: string): Promise<void> {
    return await runInInjectionContext(this.injector, async () => {
      const userDoc = doc(this.firestore, `${this.collectionName}/${userId}`);
      return await updateDoc(userDoc, {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
  }

  /**
   * Update last login timestamp and device information
   * This should be called whenever a user successfully logs in
   */
  async updateLoginInfo(userId: string): Promise<void> {
    return await runInInjectionContext(this.injector, async () => {
      const userDoc = doc(this.firestore, `${this.collectionName}/${userId}`);
      const deviceInfo = createDeviceInfo();
      return await updateDoc(userDoc, {
        lastLoginAt: serverTimestamp(),
        deviceInfo: deviceInfo,
        updatedAt: serverTimestamp()
      });
    });
  }

  /**
   * Delete an admin user
   */
  async deleteUser(userId: string): Promise<void> {
    return await runInInjectionContext(this.injector, async () => {
      const userDoc = doc(this.firestore, `${this.collectionName}/${userId}`);
      return await deleteDoc(userDoc);
    });
  }

  /**
   * Deactivate a user (soft delete)
   */
  async deactivateUser(userId: string): Promise<void> {
    return this.updateUserStatus(userId, 'deactivated');
  }

  /**
   * Activate a user
   */
  async activateUser(userId: string): Promise<void> {
    return this.updateUserStatus(userId, 'active');
  }
}

